import PropTypes from 'prop-types';

import { 
    Select,
    InputLabel,
    MenuItem,
    FormControl,
    Box,
    Typography,
    FormHelperText,
    Stack,
    Chip
} from '@mui/material';

import { useTheme } from '@emotion/react';
import { useFormContext, Controller } from 'react-hook-form';

const textFieldColor = "rgba(239, 239, 240, 1)";

const SelectionComponent = ({
    name,
    label, 
    list, 
    required=false, 
    fullWidth=true, 
    contrastTheme=false,
    showListIcons=false,
    multiple=false,
    selectedValue="",
    onChange,
    sx,
    my=2,
    ...other
}) => {
    const theme = useTheme();

    const { control } = useFormContext();

    return(
        <>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Box sx={{my: my}}>
                        <FormControl fullWidth>
                            {
                                !contrastTheme
                                ?
                                <Typography
                                    fontWeight={400}
                                    fontSize={{xs: 16, lg: 25}}
                                    letterSpacing={0.3}
                                    pb={label ? 1 : 0}
                                >
                                    {label}
                                </Typography>
                                :
                                <InputLabel id="demo-simple-select-label">
                                    <Typography color='rgba(145, 158, 171, 1)'>
                                        {label !== '' ? `${label} ${required ? '*' : ''}` : ''}
                                    </Typography>
                                </InputLabel>
                            }

                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                name={name} 
                                value={field.value}
                                multiple={multiple}
                                label={
                                    contrastTheme
                                    ?
                                    label !== '' ? `${label} ${required ? '*' : ''}` : ''
                                    :
                                    undefined
                                }
                                fullWidth={fullWidth}
                                onChange={(event) => {
                                    field.onChange(event.target.value);

                                    if(onChange) onChange(event.target.value);
                                }}
                                sx={{
                                    backgroundColor: contrastTheme ? 'white' : `${textFieldColor}`,
                                    borderRadius: '8px',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: error ? 'rgba(255, 0, 0, 1)' : contrastTheme ? 'white' : `${textFieldColor}`,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: "1px",
                                    },
                                    '&:hover fieldset': {
                                        borderColor: error ? 'rgba(255, 0, 0, 1)' : contrastTheme ? 'white' : `${textFieldColor}`,
                                    },
                                    '& .MuiSelect-select': {
                                        color: 'rgba(175, 175, 175, 1)',
                                        fontWeight: 600,
                                        fontSize: 16,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                        whiteSpace: 'normal',
                                        alignItems: 'center'
                                    },
                                    ...sx
                                }}
                                renderValue={(selected) => {
                                    if (multiple) {
                                        return list
                                            .filter((item) => selected.includes(item.value))
                                            .map((item, index) => {
                                                return <Chip
                                                    key={index}
                                                    label={item.label}
                                                    onMouseDown={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                    onDelete={(event) => {
                                                        event.stopPropagation();
                                                        const newValue = field.value.filter(v => v !== item.value);
                                                        field.onChange(newValue);
                                                        if (onChange) onChange(newValue);
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#fff2eeff'
                                                    }}
                                                />
                                            });
                                    }

                                    const selectedItem = list.find((i) => i.value === selected);

                                    return selectedItem 
                                    ? 
                                    <Stack
                                        direction='row'
                                        justifyContent='start'
                                        alignItems='center'
                                    >
                                        <Box
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                mr: 1,
                                                display: selectedItem.logoColor || selectedItem.logoColorless ? 'flex' : 'none'
                                            }}
                                        >
                                            <img 
                                                src={multiple ? (field.value?.includes?.(selectedItem.value) ? selectedItem.logoColor : selectedItem.logoColorless) : (field.value === selectedItem.value ? selectedItem.logoColor : selectedItem.logoColorless)}
                                                alt={selectedItem.label}
                                                width='100%'
                                            />
                                        </Box>

                                        {selectedItem.label}
                                    </Stack>
                                    : 
                                    selected;
                                }}
                                {...other}
                            >
                                {
                                    (multiple ? list.filter((item) => !field.value?.includes?.(item.value)) : list)?.map((item, index) => {
                                        return <MenuItem key={index} value={item.value}>
                                            {
                                                showListIcons
                                                ?
                                                <Stack
                                                    direction='row'
                                                    justifyContent='start'
                                                    alignItems='center'
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '20px',
                                                            height: '20px',
                                                            mr: 1,
                                                            display: item.logoColor || item.logoColorless ? 'flex' : 'none'
                                                        }}
                                                    >
                                                        <img 
                                                            src={multiple ? (field.value?.includes?.(item.value) ? item.logoColor : item.logoColorless) : (field.value === item.value ? item.logoColor : item.logoColorless)}
                                                            alt={item.label}
                                                            width='100%'
                                                        />
                                                    </Box>

                                                    {item.label}
                                                </Stack>
                                                :
                                                item.label
                                            }
                                        </MenuItem>
                                    })
                                }
                            </Select>

                            {
                                error
                                ?
                                <FormHelperText
                                    sx={{
                                        color: 'rgba(255, 0, 0, 1)'
                                    }}
                                >
                                    {error.message}
                                </FormHelperText>
                                :
                                <></>
                            }
                        </FormControl>
                    </Box>
                )}
            />
        </>
    );
}

export default SelectionComponent;

SelectionComponent.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    list: PropTypes.array,
    required: PropTypes.bool,
    selectedValue: PropTypes.string,
    fullWidth: PropTypes.bool,
    sx: PropTypes.object,
    onChange: PropTypes.func,
    contrastTheme: PropTypes.bool,
    showListIcons: PropTypes.bool
}